<template>
  <div class="product-detail-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ pageMode === 'create' ? $t('wholesaler.product.ProductDetail.line6_create') : (isEditing ? $t('wholesaler.product.ProductDetail.line6_edit') : $t('wholesaler.product.ProductDetail.line6_detail')) }}</h1>
      <div class="header-actions">
        <button v-if="!isEditing && product" class="btn-share" @click="showShareDialog = true">
          {{ $t('wholesaler.product.ProductDetail.line9_share') }}
        </button>
        <button v-if="isEditing" class="btn-save" :disabled="saving" @click="handleSaveClick">
          {{ saving ? $t('wholesaler.product.ProductDetail.line12_saving') : (pageMode === 'create' ? $t('wholesaler.product.ProductDetail.line12_create') : $t('wholesaler.product.ProductDetail.line12_save')) }}
        </button>
      </div>
    </header>

    <!-- 分享弹窗 -->
    <WholesalerShareDialog
      v-if="showShareDialog && product"
      type="product"
      :item-id="product.id"
      @close="showShareDialog = false"
      @created="onShareCreated"
    />

    <!-- 页面内容 -->
    <div v-if="pageMode === 'create' || product" class="page-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <!-- 基础信息 -->
      <div v-else class="info-section">
        <h3>{{ $t('wholesaler.product.ProductDetail.line36_text') }}</h3>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line39_text') }}</label>
          <div class="template-search-wrapper">
            <input 
              type="text" 
              v-model="formData.name"
              :placeholder="$t('wholesaler.product.ProductDetail.line43_placeholder')"
              maxlength="50"
              :disabled="!isEditing"
              @input="onProductNameInput"
              @focus="showTemplateDropdown = productTemplates.length > 0"
            />
            <!-- 模板下拉提示 -->
            <div v-if="showTemplateDropdown && productTemplates.length > 0" class="template-dropdown">
              <div class="template-dropdown-header">
                <span class="template-dropdown-title">模板</span>
              </div>
              <div 
                v-for="template in productTemplates" 
                :key="template.id"
                class="template-item"
                @click="selectTemplate(template)"
              >
                <img v-if="template.mainImage" :src="getImageUrl(template.mainImage)" class="template-image" />
                <div v-else class="template-image-placeholder">📦</div>
                <div class="template-info">
                  <div class="template-name">{{ template.name }}</div>
                  <div class="template-meta">
                    <span v-if="template.categoryName">{{ template.categoryName }}</span>
                    <span v-if="template.unit">{{ template.unit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line50_text') }}</label>
          <CategorySelect 
            v-model="formData.categoryName"
            :product-name="formData.name"
            :disabled="!isEditing"
          />
        </div>

        <!-- 二级分类 -->
        <div class="form-item" v-if="subCategoryList.length > 0">
          <label>{{ $t('wholesaler.product.ProductDetail.line60_text') }}</label>
          <select v-model="formData.subCategoryName" :disabled="!isEditing">
            <option value="">{{ $t('wholesaler.product.ProductDetail.line62_text') }}</option>
            <option v-for="sub in subCategoryList" :key="sub.subCategoryName" :value="sub.subCategoryName">{{ sub.subCategoryName }}</option>
          </select>
        </div>

        <!-- 仓库列表 -->
        <div class="warehouse-section">
          <div class="warehouse-header">
            <h4>{{ $t('wholesaler.product.ProductDetail.line70_text') }}</h4>
            <button v-if="isEditing" type="button" class="btn-manage" @click="goToWarehouseManagement">
              {{ $t('wholesaler.product.ProductDetail.line72_warehouse') }}
            </button>
          </div>
          
          <div class="warehouse-list">
            <!-- 店铺仓库（默认仓库）- 特殊样式 -->
            <div class="warehouse-item shop-warehouse">
              <div class="warehouse-info">
                <span class="warehouse-icon">🏪</span>
                <span class="warehouse-name">{{ $t('wholesaler.product.ProductDetail.line81_text') }}</span>
              </div>
              <div class="warehouse-stock">
                <input 
                  type="number" 
                  v-model.number="formData.shopStockQuantity"
                  @focus="recordOriginalStock"
                  @blur="onWarehouseStockBlur"
                  :disabled="!isEditing"
                  class="stock-input"
                  :placeholder="$t('wholesaler.product.ProductDetail.line91_placeholder')"
                />
                <span class="unit">{{ unit }}</span>
              </div>
            </div>
            
            <!-- 所有实际仓库列表 -->
            <div class="warehouse-item" v-for="warehouse in allWarehouses" :key="warehouse.warehouseId">
              <div class="warehouse-info">
                <span class="warehouse-icon">📦</span>
                <span class="warehouse-name">{{ warehouse.warehouseName }}</span>
              </div>
              <div class="warehouse-stock">
                <input 
                  type="number" 
                  v-model.number="warehouse.stockQuantity"
                  @focus="recordOriginalStock"
                  @blur="onWarehouseStockBlur"
                  :disabled="!isEditing"
                  class="stock-input"
                  :placeholder="$t('wholesaler.product.ProductDetail.line111_placeholder')"
                />
                <span class="unit">{{ unit }}</span>
              </div>
            </div>
          </div>
          
          <!-- 总库存 -->
          <div class="total-stock-bar">
            <strong>{{ $t('wholesaler.product.ProductDetail.line120_total_stock') }}：{{ totalWarehouseStock }} {{ unit }}</strong>
          </div>
          
          <!-- ⭐ 库存调整面板 -->
          <div v-if="showStockAdjustDialog" class="stock-adjust-hint">
            <div class="adjust-info">
              <span class="adjust-label">{{ $t('wholesaler.product.ProductDetail.line126_text') }}</span>
              <span 
                class="adjust-value"
                :class="stockAdjustment.changeQuantity > 0 ? 'positive' : 'negative'"
              >
                {{ stockAdjustment.changeQuantity > 0 ? '+' : '' }}
                {{ stockAdjustment.changeQuantity }}
                {{ unit }}
              </span>
            </div>

            <div class="adjust-form">
              <select 
                v-model="stockAdjustment.adjustType"
                class="adjust-type-select"
              >
                <option value="">{{ $t('wholesaler.product.ProductDetail.line142_text') }}</option>
                <option value="损耗">{{ $t('wholesaler.product.ProductDetail.line145_text') }}</option>
                <option value="盘点">{{ $t('wholesaler.product.ProductDetail.line146_text') }}</option>
                <option value="其他">{{ $t('wholesaler.product.ProductDetail.line147_text') }}</option>
              </select>

              <div class="adjust-flow-hints">
                <p v-if="stockAdjustment.changeQuantity > 0" class="hint-positive">💡 库存增加请通过「进货单」流程操作</p>
                <p v-else class="hint-negative">💡 库存减少请通过「订单」流程操作</p>
              </div>

              <input
                type="text"
                v-model="stockAdjustment.reason"
                :placeholder="$t('wholesaler.product.ProductDetail.line153_placeholder')"
                class="adjust-reason-input"
              />
            </div>
          </div>
          
          <!-- 空状态提示 -->
          <div v-if="!allWarehouses || allWarehouses.length === 0" class="empty-warehouse">
            <p>{{ $t('wholesaler.product.ProductDetail.line161_text') }}</p>
            <button v-if="isEditing" type="button" class="btn-add" @click="goToWarehouseManagement">
              {{ $t('wholesaler.product.ProductDetail.line163_add_warehouse') }}
            </button>
          </div>
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line169_text') }}</label>
          <select v-model="formData.unit" :disabled="!isEditing">
            <option v-for="u in unitOptions" :key="u.value" :value="u.value">
              {{ u.label }}
            </option>
          </select>
        </div>


      </div>

      <!-- 价格设置 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.product.ProductDetail.line182_text') }}</h3>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line185_text') }}</label>
          <input 
            type="number" 
            v-model.number="formData.marketPrice" 
            step="0.0001"
            :placeholder="$t('wholesaler.product.ProductDetail.line190_placeholder')"
            :disabled="!isEditing"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line196_text') }}</label>
          <input 
            type="number" 
            v-model.number="formData.costPrice" 
            step="0.0001"
            :placeholder="$t('wholesaler.product.ProductDetail.line201_placeholder')"
            :disabled="!isEditing"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line207_text') }}</label>
          <select v-model="formData.taxRateId" :disabled="!isEditing" @change="onTaxRateChange">
            <option :value="null">{{ $t('wholesaler.product.ProductDetail.line209_text') }}</option>
            <option v-for="tax in taxRates" :key="tax.id" :value="tax.id">
              {{ tax.name }} ({{ (tax.rate * 100).toFixed(1) }}%)
            </option>
          </select>
          <div v-if="formData.taxRateId" class="tax-hint">
            {{ $t('wholesaler.product.ProductDetail.line215_tax_hint', { name: getTaxName(formData.taxRateId) }) }}
          </div>
        </div>
      </div>



      <!-- 其他设置 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.product.ProductDetail.line224_text') }}</h3>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line227_text') }}</label>
          <select v-model="formData.enabled" :disabled="!isEditing">
            <option :value="true">{{ $t('wholesaler.product.ProductDetail.line229_text') }}</option>
            <option :value="false">{{ $t('wholesaler.product.ProductDetail.line230_text') }}</option>
          </select>
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line235_text') }}</label>
          <select v-model="formData.wholesalePriceVisible" :disabled="!isEditing">
            <option :value="false">{{ $t('wholesaler.product.ProductDetail.line237_text') }}</option>
            <option :value="true">{{ $t('wholesaler.product.ProductDetail.line238_text') }}</option>
          </select>
        </div>

        <!-- 置顶设置 -->
        <div class="pin-section">
          <h4>{{ $t('wholesaler.product.ProductDetail.line244_text') }}</h4>
          <div class="pin-options">
            <label class="pin-option">
              <input 
                type="checkbox" 
                v-model="formData.pinnedHome" 
                :disabled="!isEditing"
                @change="onPinHomeChange"
              />
              <span class="pin-label">{{ $t('wholesaler.product.ProductDetail.line253_text') }}</span>
              <span class="pin-time" v-if="formData.pinnedHomeAt">({{ formatDateTime(formData.pinnedHomeAt) }})</span>
            </label>
            <label class="pin-option">
              <input 
                type="checkbox" 
                v-model="formData.pinnedSearch" 
                :disabled="!isEditing"
                @change="onPinSearchChange"
              />
              <span class="pin-label">{{ $t('wholesaler.product.ProductDetail.line263_text') }}</span>
              <span class="pin-time" v-if="formData.pinnedSearchAt">({{ formatDateTime(formData.pinnedSearchAt) }})</span>
            </label>
          </div>
        </div>

        <!-- 商品标签 -->
        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line271_text') }}</label>
          <div class="tag-input-wrapper">
            <span 
              v-for="tag in tagList" 
              :key="tag" 
              class="tag-item"
            >
              {{ tag }}
              <span v-if="isEditing" class="tag-remove" @click="removeTag(tag)">×</span>
            </span>
            <input 
              v-if="isEditing && tagInputVisible"
              ref="tagInputRef"
              v-model="tagInputValue"
              class="tag-input"
              :placeholder="$t('wholesaler.product.ProductDetail.line286_placeholder')"
              @keyup.enter="addTag"
              @blur="addTag"
              maxlength="20"
            />
            <button 
              v-if="isEditing && !tagInputVisible" 
              class="btn-add-tag"
              @click="showTagInput"
            >
              {{ $t('wholesaler.product.ProductDetail.line296_add_tag') }}
            </button>
          </div>
          <div class="hint-text">{{ $t('wholesaler.product.ProductDetail.line299_text') }}</div>
        </div>

        <!-- 搜索别名 -->
        <div class="form-item">
          <label>{{ $t('wholesaler.product.ProductDetail.line304_text') }}</label>
          <div class="tag-input-wrapper">
            <span 
              v-for="alias in aliasList" 
              :key="alias" 
              class="tag-item alias"
            >
              {{ alias }}
              <span v-if="isEditing" class="tag-remove" @click="removeAlias(alias)">×</span>
            </span>
            <input 
              v-if="isEditing && aliasInputVisible"
              ref="aliasInputRef"
              v-model="aliasInputValue"
              class="tag-input"
              :placeholder="$t('wholesaler.product.ProductDetail.line319_placeholder')"
              @keyup.enter="addAlias"
              @blur="addAlias"
              maxlength="30"
            />
            <button 
              v-if="isEditing && !aliasInputVisible" 
              class="btn-add-tag"
              @click="showAliasInput"
            >
              {{ $t('wholesaler.product.ProductDetail.line329_add_alias') }}
            </button>
          </div>
          <div class="hint-text">{{ $t('wholesaler.product.ProductDetail.line332_text') }}</div>
        </div>
      </div>

      <!-- 商品描述 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.product.ProductDetail.line338_text') }}</h3>
        <textarea 
          v-model="formData.description" 
          :placeholder="$t('wholesaler.product.ProductDetail.line341_placeholder')"
          rows="4"
          :disabled="!isEditing"
          class="description-textarea"
        ></textarea>
      </div>

      <!-- 商品图片 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.product.ProductDetail.line350_text') }}</h3>
        <div class="image-gallery">
          <div v-if="mainImages.length > 0" class="preview-grid">
            <div v-for="(img, idx) in mainImages" :key="idx" class="image-item">
              <img :src="getImageUrl(img)" alt="" class="gallery-img" @click="openImageViewer(idx)" />
              <button v-if="isEditing" class="btn-delete-image" @click.stop="deleteImage(idx)">×</button>
            </div>
          </div>
          <div v-else class="no-image">{{ $t('wholesaler.product.ProductDetail.line358_text') }}</div>
        </div>
        <div class="image-actions">
          <button v-if="isEditing" @click="handleMainImageUpload" class="btn-upload">{{ $t('wholesaler.product.ProductDetail.line361_text') }}</button>
        </div>
      </div>

      <!-- 库存流水（最近 3 条） -->
      <div v-if="pageMode === 'edit' && productId" class="info-section stock-flow-section">
        <div class="section-header">
          <h3>{{ $t('wholesaler.product.ProductDetail.line368_text') }}</h3>
          <button class="btn-more" @click="goToStockFlowQuery">{{ $t('wholesaler.product.ProductDetail.line369_text') }}</button>
        </div>
        <div v-if="stockHistoryLoading" class="stock-flow-empty">{{ $t('common.loading') }}</div>
        <div v-else-if="stockHistory.length === 0" class="stock-flow-empty">{{ $t('wholesaler.product.ProductDetail.line372_text') }}</div>
        <div v-else class="stock-flow-list">
          <div v-for="item in stockHistory.slice(0, 3)" :key="item.id" class="stock-flow-item">
            <div class="flow-header">
              <span class="flow-type" :class="item.adjustReason === '入库' ? 'in' : 'out'">{{ translateFlowType(item.adjustReason) }}</span>
              <span class="flow-date">{{ formatDate(item.createdAt) }}</span>
            </div>
            <div class="flow-body">
              <span class="flow-warehouse">{{ item.warehouseName || $t('wholesaler.product.ProductDetail.line380_shop_warehouse') }}</span>
              <span class="flow-change" :class="item.adjustQuantity >= 0 ? 'positive' : 'negative'">
                {{ item.adjustQuantity >= 0 ? '+' : '' }}{{ item.adjustQuantity }}
              </span>
              <span class="flow-before-after">{{ item.beforeQuantity }} → {{ item.afterQuantity }}</span>
            </div>
            <div v-if="item.reason" class="flow-reason">{{ $t('wholesaler.product.ProductDetail.line386_remark') }}{{ item.reason }}</div>
          </div>
        </div>
      </div>
    </div>

    
    <!-- Multi-Warehouse Dialog -->
    <MultiWarehouseDialog
      :visible="showMultiWarehouseDialog"
      :product-id="parseInt(productId)"
      :wholesaler-id="parseInt(wholesalerId)"
      :current-stock="formData.totalStock"
      :shop-stock-quantity="formData.shopStockQuantity"
      @update:visible="showMultiWarehouseDialog = $event"
      @saved="handleMultiWarehouseSaved"
    />

    <!-- Toast -->
    <div v-if="showToast" class="toast show">{{ toastMessage }}</div>

    <!-- 图片查看器 -->
    <div v-if="showImageViewer" class="image-viewer show" @click="closeImageViewer">
      <div class="viewer-container">
        <button class="viewer-close" @click.stop="closeImageViewer">×</button>
        <img :src="getImageUrl(currentViewImage)" class="viewer-image" />
      </div>
    </div>

    <!-- 库存调整确认弹窗 -->
    <ConfirmModal
      :visible="showConfirmModal"
      :title="$t('wholesaler.product.ProductDetail.line418_title')"
      :message="$t('wholesaler.product.ProductDetail.line419_message')"
      :confirm-text="$t('wholesaler.product.ProductDetail.line420_confirm')"
      :cancel-text="$t('wholesaler.product.ProductDetail.line421_cancel')"
      @confirm="confirmSaveWithoutReason"
      @cancel="cancelSave"
    />

  </div>
</template>

<script>
import api from '@/services/api';
import CategorySelect from '@/components/CategorySelect.vue';
import MultiWarehouseDialog from '@/components/MultiWarehouseDialog.vue';
import WholesalerShareDialog from '@/components/WholesalerShareDialog.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { getWholesalerId } from '@/utils/wholesaler';
import { getImageUrl } from '@/utils/image';

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default {
  name: 'ProductDetail',
  
  components: {
    CategorySelect,
    MultiWarehouseDialog,
    ConfirmModal
  },
  
  data() {
    return {
      productId: null,
      isEditing: false,
      product: null,
      loading: false,
      pageMode: 'edit',
      
      // 库存信息（从库存系统联动）
      inventories: [],
      stockHistory: [],
      stockHistoryLoading: false,
      productList: [],  // 商品列表（用于流水查询页跳转）
      
      // 库存调整相关
      originalStock: null, // 原始库存（用于对比变化）
      originalTotalStock: null, // 原始总库存（用于对比变化）
      showStockAdjustDialog: false, // 是否显示库存调整对话框
      stockAdjustment: {
        changeQuantity: 0, // 变化数量（只读）
        adjustType: '', // 调整类型：进货/出库/损耗/其他
        reason: '' // 原因说明
      },
      
      formData: {
        id: null,
        name: '',
        code: '',
        description: '',
        marketPrice: null,
        costPrice: null,
        unit: '件 (Piece)',
        categoryName: '',
        enabled: true,
        isHot: false,
        isNew: false,
        shopStockQuantity: 0,  // ⭐ 初始化店铺库存
        totalStock: 0,
        warehouses: [],
        mainImages: [],
        taxRateId: null,  // ⭐ 税率 ID
        pinnedHome: false,  // ⭐ 置顶到首页
        pinnedHomeAt: null,
        pinnedSearch: false,  // ⭐ 置顶到搜索
        pinnedSearchAt: null,
        wholesalePriceVisible: true,  // ⭐ 仅设置批发价的档口可见（默认开启）
        subCategoryName: '',   // 二级分类
        tags: '',              // 标签（逗号分隔）
        aliases: ''            // 别名（逗号分隔）
      },
      
      // 商品模板搜索相关
      productTemplates: [],  // 模板列表
      showTemplateDropdown: false,  // 是否显示模板下拉
      templateSearchTimer: null,  // 防抖定时器
      taxRates: [],  // ⭐ 税率列表
      saving: false,  // ⭐ 保存中状态
      
      // 标签/别名输入状态
      tagInputVisible: false,
      tagInputValue: '',
      aliasInputVisible: false,
      aliasInputValue: '',
      
      // 二级分类选项（从数据库获取）
      subCategoryList: [],
      
      showToast: false,
      toastMessage: '',
      
      currentViewImage: '',
      showImageViewer: false,
      showMultiWarehouseDialog: false,
      showShareDialog: false,
      showConfirmModal: false,
      wholesalerId: null
    };
  },
  
  computed: {
    // 二级分类选项（从数据库获取）
    subCategoryOptions() {
      return this.subCategoryList
    },
    
    // 标签列表（字符串转数组）
    tagList() {
      if (!this.formData.tags) return []
      return this.formData.tags.split(',').filter(t => t.trim())
    },
    
    // 别名列表（字符串转数组）
    aliasList() {
      if (!this.formData.aliases) return []
      return this.formData.aliases.split(',').filter(a => a.trim())
    },
    
    mainImages() {
      return this.formData.mainImages || [];
    },
    
    // ⭐ 所有实际仓库列表（用于显示）
    allWarehouses() {
      return this.formData.warehouses || [];
    },
    
    // ⭐ 总库存 = 店铺库存 + 所有实际仓库库存
    totalWarehouseStock() {
      const shopStock = Number(this.formData.shopStockQuantity) || 0;
      const warehouseStock = (this.formData.warehouses || []).reduce(
        (sum, w) => sum + (Number(w.stockQuantity) || 0), 
        0
      );
      return Number((shopStock + warehouseStock).toFixed(1));
    },
    
    unit() {
      const u = this.formData.unit || '件 (Item)';
      const bilingualMap = {
        '斤': '斤 (Catty)', '千克': '千克 (Kilogram)', '箱': '箱 (Carton)',
        '袋': '袋 (Bag)', '盒': '盒 (Box)', '包': '包 (Pack)',
        '瓶': '瓶 (Bottle)', '桶': '桶 (Bucket)', '盘': '盘 (Tray)',
        '个': '个 (Piece)', '粒': '粒 (Grain)', '件': '件 (Item)',
        '枚': '枚 (Piece)', '颗': '颗 (Piece)', '打': '打 (Dozen)',
        '板': '板 (Board)', '托盘': '托盘 (Pallet)', '其他': '其他 (Other)'
      };
      return bilingualMap[u] || u;
    },
    
    // 计量单位选项（中英双语，value 即为显示文本）
    unitOptions() {
      return [
        { value: '斤 (Catty)', label: '斤 (Catty)' },
        { value: '千克 (Kilogram)', label: '千克 (Kilogram)' },
        { value: '箱 (Carton)', label: '箱 (Carton)' },
        { value: '袋 (Bag)', label: '袋 (Bag)' },
        { value: '盒 (Box)', label: '盒 (Box)' },
        { value: '包 (Pack)', label: '包 (Pack)' },
        { value: '瓶 (Bottle)', label: '瓶 (Bottle)' },
        { value: '桶 (Bucket)', label: '桶 (Bucket)' },
        { value: '盘 (Tray)', label: '盘 (Tray)' },
        { value: '个 (Piece)', label: '个 (Piece)' },
        { value: '粒 (Grain)', label: '粒 (Grain)' },
        { value: '枚 (Piece)', label: '枚 (Piece)' },
        { value: '颗 (Piece)', label: '颗 (Piece)' },
        { value: '打 (Dozen)', label: '打 (Dozen)' },
        { value: '件 (Item)', label: '件 (Item)' },
        { value: '板 (Board)', label: '板 (Board)' },
        { value: '托盘 (Pallet)', label: '托盘 (Pallet)' },
        { value: '其他 (Other)', label: '其他 (Other)' }
      ];
    }
  },
  
  watch: {
    // ⭐ 监听分类变化，确保 AI 推荐后正确同步
    'formData.categoryName': {
      handler(newVal, oldVal) {
        console.log('🔍 [Watch] 分类变化:', newVal, '旧值:', oldVal, '长度:', newVal?.length);
        // 从数据库获取二级分类
        if (newVal) {
          this.loadSubCategories(newVal)
        } else {
          this.subCategoryList = []
          this.formData.subCategoryName = ''
        }
      },
      immediate: true
    },
    // ⭐ 税率 ID 变化时同步税率信息
    'formData.taxRateId': function() {
      this.syncTaxById();
    },
    // ⭐ 税率列表异步加载后同步
    taxRates: function() {
      this.syncTaxById();
    }
  },
  
  async mounted() {
    // 点击外部关闭模板下拉
    document.addEventListener('click', this.handleClickOutside);
    // 检查是否是新建模式
    const isCreateMode = this.$route.path === '/wholesaler/product/create' || !this.$route.params.id;
    this.pageMode = isCreateMode ? 'create' : 'edit';
    this.isEditing = true;
    
    // ⭐ 确保 productId 是有效值（防止 NaN）
    const routeId = this.$route.params.id;
    this.productId = routeId && !isNaN(Number(routeId)) ? Number(routeId) : null;
    
    console.log('📄 页面模式:', this.pageMode, 'Product ID:', this.productId);
    
    // ⭐ 初始化批发商 ID（必须先完成，后续接口依赖）
    await this.initWholesalerId();
    
    // ⭐ 根据模式初始化
    if (this.pageMode === 'edit' && this.productId) {
      this.loadProductDetail();
    } else {
      // 创建模式：初始化仓库列表
      this.initWarehousesForCreate();
    }
  },
  
  // ⭐ 从仓库管理页面返回时重新加载仓库列表
  activated() {
    if (this.productId && this.pageMode === 'edit') {
      console.log('🔄 页面激活，重新加载仓库数据...');
      this.loadWarehouseStocks();
    }
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  methods: {
    // 点击外部关闭模板下拉
    handleClickOutside(event) {
      const wrapper = this.$el.querySelector('.template-search-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        this.showTemplateDropdown = false;
      }
    },
    
    // 商品名称输入防抖搜索
    onProductNameInput() {
      if (this.templateSearchTimer) {
        clearTimeout(this.templateSearchTimer);
      }
      const keyword = this.formData.name?.trim();
      if (!keyword || keyword.length < 2) {
        this.productTemplates = [];
        this.showTemplateDropdown = false;
        return;
      }
      this.templateSearchTimer = setTimeout(() => {
        this.searchProductTemplates(keyword);
      }, 300);
    },
    
    // 搜索商品模板
    async searchProductTemplates(keyword) {
      try {
        const res = await api.get('/product/product-templates', { params: { keyword } });
        if (res && Array.isArray(res)) {
          this.productTemplates = res;
          this.showTemplateDropdown = res.length > 0;
        } else {
          this.productTemplates = [];
          this.showTemplateDropdown = false;
        }
      } catch (e) {
        console.error('搜索商品模板失败:', e);
        this.productTemplates = [];
        this.showTemplateDropdown = false;
      }
    },
    
    // 选择模板自动填充
    selectTemplate(template) {
      // 填充名称
      this.formData.name = template.name;
      // 填充分类
      if (template.categoryName) {
        this.formData.categoryName = template.categoryName;
      }
      // 填充二级分类
      if (template.subCategoryName) {
        this.formData.subCategoryName = template.subCategoryName;
      }
      // 填充单位
      if (template.unit) {
        this.formData.unit = template.unit;
      }
      // 填充税率
      if (template.taxRate !== null && template.taxRate !== undefined) {
        this.formData.taxRate = template.taxRate;
      }
      // 填充备注
      if (template.remark) {
        this.formData.remark = template.remark;
      }
      // 填充图片（优先 images 多图，回退到 mainImage 单图）
      if (template.images) {
        try {
          const imagesArray = typeof template.images === 'string' 
            ? JSON.parse(template.images) 
            : template.images;
          if (Array.isArray(imagesArray) && imagesArray.length > 0) {
            this.formData.mainImages = imagesArray
              .filter(url => url)
              .map(url => this.normalizeImageUrl(url));
            console.log('✅ 模板图片已加载（多图）:', this.formData.mainImages);
          } else if (template.mainImage) {
            const imageUrl = this.normalizeImageUrl(template.mainImage);
            this.formData.mainImages = [imageUrl];
            console.log('✅ 模板图片已加载（单图）:', imageUrl);
          }
        } catch (e) {
          console.error('解析模板图片失败:', e);
          if (template.mainImage) {
            this.formData.mainImages = [this.normalizeImageUrl(template.mainImage)];
          }
        }
      } else if (template.mainImage) {
        const imageUrl = this.normalizeImageUrl(template.mainImage);
        this.formData.mainImages = [imageUrl];
        console.log('✅ 模板图片已加载（单图）:', imageUrl);
      }
      // 填充标签
      if (template.tags) {
        this.formData.tags = template.tags;
      }
      // 填充别名
      if (template.aliases) {
        this.formData.aliases = template.aliases;
      }
      // 填充描述
      if (template.description) {
        this.formData.description = template.description;
      }
      // 隐藏下拉
      this.showTemplateDropdown = false;
      this.productTemplates = [];
      // 显示模板加载成功提示
      this.showToastMessage('✅ 已加载模板：' + template.name);
    },
    
    // 显示提示消息
    showToastMessage(message) {
      this.toastMessage = message;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
    
    // 标签相关方法
    showTagInput() {
      this.tagInputVisible = true
      this.$nextTick(() => {
        this.$refs.tagInputRef?.focus()
      })
    },
    addTag() {
      const val = this.tagInputValue.trim()
      if (val && !this.tagList.includes(val)) {
        const list = [...this.tagList, val]
        this.formData.tags = list.join(',')
      }
      this.tagInputValue = ''
      this.tagInputVisible = false
    },
    removeTag(tag) {
      const list = this.tagList.filter(t => t !== tag)
      this.formData.tags = list.join(',')
    },
    
    // 别名相关方法
    showAliasInput() {
      this.aliasInputVisible = true
      this.$nextTick(() => {
        this.$refs.aliasInputRef?.focus()
      })
    },
    addAlias() {
      const val = this.aliasInputValue.trim()
      if (val && !this.aliasList.includes(val)) {
        const list = [...this.aliasList, val]
        this.formData.aliases = list.join(',')
      }
      this.aliasInputValue = ''
      this.aliasInputVisible = false
    },
    removeAlias(alias) {
      const list = this.aliasList.filter(a => a !== alias)
      this.formData.aliases = list.join(',')
    },
    
    // 从数据库加载二级分类
    async loadSubCategories(categoryName) {
      try {
        const res = await api.get('/product/sub-categories', {
          params: { categoryName }
        })
        this.subCategoryList = Array.isArray(res) ? res : (res?.data || [])
        console.log('✅ 加载二级分类:', categoryName, this.subCategoryList)
      } catch (error) {
        console.error('❌ 加载二级分类失败:', error)
        this.subCategoryList = []
      }
    },
    
    getImageUrl,
    // ⭐ 图片 URL 规范化：将绝对 URL 转为相对路径
    normalizeImageUrl(url) {
      if (!url) return url;
      // 如果是完整 URL，提取路径部分
      if (url.startsWith('http')) {
        try {
          const urlObj = new URL(url);
          return urlObj.pathname; // 返回 /api/uploads/... 格式
        } catch (e) {
          return url;
        }
      }
      return url;
    },
    
    // ⭐ 分享创建成功回调
    onShareCreated(shareData) {
      console.log('分享链接已创建:', shareData);
    },
    
    // ⭐ 初始化批发商 ID
    async initWholesalerId() {
      try {
        const response = await api.get('/auth/me');
        if (!response.wholesalerId) {
          throw new Error('未关联批发商，请先选择批发商');
        }
        this.wholesalerId = response.wholesalerId;
        console.log('Wholesaler ID:', this.wholesalerId);
      } catch (error) {
        console.error('获取批发商ID失败:', error);
        throw error;
      }
    },
    
    // ⭐ 创建模式：初始化仓库列表
    async initWarehousesForCreate() {
      try {
        // 加载税率列表
        try {
          const taxRes = await api.get('/admin/tax-rates/enabled');
          this.taxRates = Array.isArray(taxRes) ? taxRes : (taxRes.data || []);
          // 自动选中默认税率
          const defaultTax = this.taxRates.find(t => t.isDefault);
          if (defaultTax) {
            this.formData.taxRateId = Number(defaultTax.id);
            this.formData.taxRate = defaultTax.rate;
            this.formData.taxName = defaultTax.name;
          }
        } catch (e) { this.taxRates = []; }
        
        // 获取仓库列表
        const warehouseListRes = await api.get('/warehouse/list', {
          params: { wholesalerId: this.wholesalerId }
        });
        
        const allWarehouses = Array.isArray(warehouseListRes) ? warehouseListRes : [];
        
        // 2️⃣ 构造仓库结构（默认库存 0）
        this.formData.warehouses = allWarehouses.map(wh => ({
          id: null,
          warehouseId: wh.id,
          warehouseName: wh.warehouseName,
          stockQuantity: 0
        }));
        
        // 3️⃣ 店铺仓库默认 0
        this.formData.shopStockQuantity = 0;
        
        // 4️⃣ 初始化原始快照
        this.originalWarehouses = this.formData.warehouses.map(w => ({ ...w }));
        this.originalTotalStock = 0;
        
        console.log('✅ 创建模式仓库初始化完成');
        console.log('   仓库数量:', this.formData.warehouses.length);
        console.log('   店铺仓库库存:', this.formData.shopStockQuantity);
        this.formData.warehouses.forEach((w, i) => {
          console.log(`   [${i}] ${w.warehouseName}: ${w.stockQuantity}`);
        });
      } catch (error) {
        console.error('❌ 初始化仓库失败:', error);
      }
    },
    
    // ⭐ 跳转到仓库管理页面
    goToWarehouseManagement() {
      this.$router.push('/wholesaler/warehouse');
    },

    // ⭐ 税率选择变化（同步快照）
    onTaxRateChange() {
      this.syncTaxById();
    },

    syncTaxById() {
      const id = this.formData.taxRateId == null ? null : Number(this.formData.taxRateId);
      const tax = this.taxRates.find(t => Number(t.id) === id);
      if (tax) {
        this.formData.taxRate = Number(tax.rate);
        this.formData.taxName = tax.name;
      } else {
        // taxRateId 为 null 时，表示"不征税"
        this.formData.taxRate = 0;
        this.formData.taxName = '不征税';
      }
    },

    // ⭐ 根据 taxRateId 现场计算税率（提交时必用，不信 formData 缓存）
    getTaxInfo() {
      const taxId = this.formData.taxRateId == null ? null : Number(this.formData.taxRateId);
      const tax = this.taxRates.find(t => Number(t.id) === taxId) || null;
      return {
        taxRateId: taxId,
        taxRate: tax ? Number(tax.rate) : 0,
        taxName: tax ? tax.name : '不征税'
      };
    },

    // ⭐ 获取税率对象（根据taxRateId从taxRates查找）
    getTaxObject() {
      const taxId = this.formData.taxRateId == null ? null : Number(this.formData.taxRateId);
      return this.taxRates.find(t => Number(t.id) === taxId) || null;
    },

    getTaxName(taxRateId) {
      const tax = this.taxRates.find(t => t.id === Number(taxRateId));
      return tax ? tax.name : '';
    },

    handleSaveClick() {
      // 检查库存变化但未填写原因
      if (this.showStockAdjustDialog && !this.formData.hasMultiWarehouse) {
        const hasType = !!this.stockAdjustment.adjustType;
        const hasReason = !!this.stockAdjustment.reason?.trim();
        
        if (!hasType || !hasReason) {
          this.showConfirmModal = true;
          return;
        }
      }
      
      if (this.pageMode === 'create') {
        this.createProduct();
      } else {
        this.saveProduct();
      }
    },
    
    // 确认继续保存（不填写原因）
    async confirmSaveWithoutReason() {
      this.showConfirmModal = false;
      
      // 设置默认原因
      if (!this.stockAdjustment.adjustType) {
        this.stockAdjustment.adjustType = '盘点调整';
      }
      if (!this.stockAdjustment.reason?.trim()) {
        this.stockAdjustment.reason = '库存调整';
      }
      
      // 继续保存
      if (this.pageMode === 'create') {
        await this.createProduct();
      } else {
        await this.saveProduct();
      }
    },
    
    // 取消保存，返回填写
    cancelSave() {
      this.showConfirmModal = false;
      this.showToastMsg('请填写库存调整类型和原因');
    },
    
    
    // 加载库存流水（最近 3 条）
    async loadStockHistory() {
      try {
        // ⭐ 创建模式不加载库存流水
        if (this.pageMode === 'create' || !this.productId) {
          this.stockHistory = [];
          return;
        }
        
        this.stockHistoryLoading = true;
        const response = await api.get(`/inventory/product/${this.productId}/records`, {
          params: { page: 1, pageSize: 3 }
        });
        
        if (Array.isArray(response.data)) {
          this.stockHistory = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.stockHistory = response.data.data;
        } else {
          this.stockHistory = [];
        }
      } catch (error) {
        console.error('加载库存流水失败:', error);
        this.stockHistory = [];
      } finally {
        this.stockHistoryLoading = false;
      }
    },

    // ⭐ 加载商品详情（含税率）
    async loadProductDetail() {
      // ⭐ 验证 productId 是否有效
      if (!this.productId || isNaN(this.productId)) {
        console.error('❌ 无效的 productId:', this.productId);
        this.showToastMsg('商品ID无效，无法加载商品信息');
        return;
      }
      
      try {
        // 1. 加载税率列表
        try {
          const taxRes = await api.get('/admin/tax-rates/enabled');
          this.taxRates = Array.isArray(taxRes) ? taxRes : (taxRes.data || []);
        } catch (e) {
          console.warn('⚠️ 税率列表加载失败:', e);
          this.taxRates = [];
        }
        
        // 2. 加载商品详情
        console.log('📄 加载商品详情，productId:', this.productId);
        const result = await api.get(`/product/${this.productId}`);
        this.product = result;
        Object.assign(this.formData, result);
        
        // ⭐ 确保 wholesalePriceVisible 有默认值（旧商品可能为 null）
        if (this.formData.wholesalePriceVisible === null || this.formData.wholesalePriceVisible === undefined) {
          this.formData.wholesalePriceVisible = true;
        }
        
        // 3. 税率回显（设置 ID 后立即同步，不信 Object.assign 带来的旧值）
        this.formData.taxRateId = result.taxRateId ? Number(result.taxRateId) : null;
        this.syncTaxById();  // ⭐⭐ 关键：立即从 taxRates 查正确的 rate/name
        
        // 4. 商品没有税率时自动选中默认税率（仅新建商品，编辑时不修改）
        if (!this.formData.taxRateId && this.pageMode === 'create') {
          const defaultTax = this.taxRates.find(t => t.isDefault);
          if (defaultTax) {
            this.formData.taxRateId = Number(defaultTax.id);
            this.formData.taxRate = defaultTax.rate;
            this.formData.taxName = defaultTax.name;
            console.log('📊 自动选中默认税率:', defaultTax.name);
          }
        }
        
        // 确保分类字段有值
        if (!this.formData.categoryName) {
          this.formData.categoryName = result.categoryName || '';
        }
        
        // 确保新字段有值
        if (this.formData.subCategoryName === undefined) {
          this.formData.subCategoryName = result.subCategoryName || '';
        }
        if (this.formData.tags === undefined) {
          this.formData.tags = result.tags || '';
        }
        if (this.formData.aliases === undefined) {
          this.formData.aliases = result.aliases || '';
        }
        
        // ⭐ 将 mainImage + images 转为数组（前端使用数组）
        if (result.images) {
          // ⭐ 优先从 images JSON 字符串解析多图
          let imgList = result.images;
          if (typeof imgList === 'string') {
            try { imgList = JSON.parse(imgList); } catch(e) { imgList = []; }
          }
          if (Array.isArray(imgList) && imgList.length > 0) {
            this.formData.mainImages = imgList
              .filter(url => url && url.startsWith('http'))
              .map(url => this.normalizeImageUrl(url));
            console.log('🖼️ 多图加载:', this.formData.mainImages);
          }
        }
        
        // 如果 images 为空，回退到 mainImage
        if (this.formData.mainImages.length === 0 && result.mainImage) {
          const imageUrl = this.normalizeImageUrl(result.mainImage);
          this.formData.mainImages = [imageUrl];
          console.log('🖼️ 单图加载:', this.formData.mainImages);
        }
        
        // ⭐ 加载库存（后端返回 shopStockQuantity 和 totalStock）
        if (result.shopStockQuantity !== undefined) {
          this.formData.shopStockQuantity = result.shopStockQuantity;
          console.log('📦 店铺库存加载:', this.formData.shopStockQuantity);
        }
        if (result.totalStock !== undefined) {
          this.formData.totalStock = result.totalStock;
          console.log('📦 总库存加载:', this.formData.totalStock);
        }
        
        // ⭐ 加载仓库库存数据（显示所有仓库及库存）
        await this.loadWarehouseStocks();
        
        console.log('📝 分类加载:', this.formData.categoryName);
        
        // 先设置为编辑模式（确保能编辑）
        this.isEditing = true;
        
        this.showToastMsg('✅ 加载成功');
        
        // 加载库存流水
        this.loadStockHistory();
      } catch (error) {
        console.error('❌ 加载失败:', error);
        this.showToastMsg(error.response?.data?.message || error.message || '网络错误');
      }
    },

    // ⭐ 加载仓库库存数据（加载所有仓库，没有库存的默认为 0）
    async loadWarehouseStocks() {
      // 创建模式下不加载仓库库存
      if (this.pageMode === 'create') {
        console.log('⏭️ 创建模式，跳过加载仓库库存');
        return;
      }
      
      try {
        console.log('🏭 加载仓库库存数据...');
        
        // 1. 先获取所有仓库列表
        const warehouseListRes = await api.get('/warehouse/list', { 
          params: { wholesalerId: this.wholesalerId } 
        });
        const allWarehouses = Array.isArray(warehouseListRes) ? warehouseListRes : [];
        console.log('📦 批发商所有仓库:', allWarehouses);
        
        // 2. 获取商品的仓库库存
        const stockRes = await api.get(`/warehouse/product/${this.productId}/stocks`);
        const stocks = Array.isArray(stockRes) ? stockRes : [];
        
        console.log('🔍 诊断仓库数据：');
        console.log('   stocks:', JSON.stringify(stocks, null, 2));
        
        // 3. ⭐ 店铺仓库库存由 loadProductDetail 已从商品实体加载，这里不再覆盖
        //    （商品表 shop_stock_quantity 和仓库表 warehouseId=null 的 stock_quantity 可能不同步）
        
        // 4. ⭐ 再处理"真正的仓库"（排除 warehouseId === null）
        this.formData.warehouses = allWarehouses.map(wh => {
          const stock = stocks.find(s => s.warehouseId === wh.id);
          return {
            id: stock ? stock.id : null,
            warehouseId: wh.id,
            warehouseName: wh.warehouseName,
            stockQuantity: stock ? Number(stock.stockQuantity) : 0
          };
        });
        
        console.log('   实际仓库数量:', this.formData.warehouses.length);
        this.formData.warehouses.forEach((w, i) => {
          console.log(`     [${i}] ${w.warehouseName}: ${w.stockQuantity}`);
        });
        
        // ⭐ 保存原始数据用于对比变化
        this.originalWarehouses = this.formData.warehouses.map(w => ({...w}));
        this.originalTotalStock = this.totalWarehouseStock;
        console.log('✅ 仓库数据加载成功:', this.formData.warehouses.length, '个仓库');
        console.log('   originalTotalStock:', this.originalTotalStock);
        console.log('   totalWarehouseStock:', this.totalWarehouseStock);
        console.log('   shopStockQuantity:', this.formData.shopStockQuantity);
      } catch (error) {
        console.error('❌ 加载仓库库存失败:', error);
        this.formData.warehouses = [];
      }
    },
    
    // ⭐ 库存输入框聚焦时，记录原始库存
    onStockFocus() {
      if (this.formData.totalStock !== null && this.formData.totalStock !== undefined) {
        this.originalStock = this.formData.totalStock;
        console.log('📦 库存聚焦，原始库存:', this.originalStock);
      }
    },
    
    // ⭐ 库存输入框失焦时，计算变化并显示调整面板
    onStockBlur() {
      if (!this.isEditing || this.formData.hasMultiWarehouse) {
        return;
      }
      
      const newStock = this.formData.totalStock || 0;
      const oldStock = this.originalStock !== null ? this.originalStock : newStock;
      
      // 如果库存有变化
      if (newStock !== oldStock && this.originalStock !== null) {
        // ⭐ 修复浮点数精度问题：50.5 - 50.1 = 0.3999999... → 0.4
        const changeQuantity = Number((newStock - oldStock).toFixed(1));
        this.stockAdjustment.changeQuantity = changeQuantity;
        // ⭐ 只在首次变化时重置，保留用户已选择的类型
        if (!this.showStockAdjustDialog) {
          this.stockAdjustment.adjustType = '';
          this.stockAdjustment.reason = '';
        }
        this.showStockAdjustDialog = true;
        
        console.log('📦 库存变化：', oldStock, '->', newStock, '变化:', changeQuantity);
      }
    },
    
    handleMultiWarehouseSaved(payload) {
      console.log('✅ 多仓库已启用，payload:', payload);
      
      // 保存仓库信息到 formData
      this.formData.warehouses = payload.warehouses;
      this.formData.shopStockQuantity = payload.shopStockQuantity;
      this.formData.totalStock = payload.totalStock;
      
      // 设置多仓库标志
      this.formData.hasMultiWarehouse = payload.hasMultiWarehouse;
      
      // 关闭对话框
      this.showMultiWarehouseDialog = false;
      
      // ⭐ 如果有库存变更，复用 saveMultiWarehouse 方法保存
      if (payload.hasChanges) {
        this.showToastMsg('📝 检测到库存变更，正在保存...');
        this.saveMultiWarehouse();
      } else {
        this.showToastMsg('✅ 多仓库已配置，请点击"保存"按钮提交');
      }
    },
    
    // 构造库存流水记录（复用）
    buildStockFlowRecords(payload) {
      const records = [];
      const originalWarehouses = payload.originalWarehouses || [];
      const adjustType = payload.adjustType || '盘点调整';
      const reason = payload.adjustReason || '多仓库存调整';
      
      // 店铺仓库变化
      const originalShop = originalWarehouses.find(w => !w.warehouseId);
      const beforeShop = originalShop?.stockQuantity || 0;
      const afterShop = payload.shopStockQuantity || 0;
      if (beforeShop !== afterShop) {
        records.push({
          productId: Number(this.productId),
          wholesalerId: Number(this.wholesalerId),
          warehouseId: null,
          warehouseName: '店铺仓库',
          adjustType: adjustType,
          changeQuantity: afterShop - beforeShop,
          beforeQuantity: beforeShop,
          afterQuantity: afterShop,
          reason: reason
        });
      }
      
      // 分仓库变化
      if (payload.warehouseStocks) {
        payload.warehouseStocks.forEach(ws => {
          if (ws.warehouseId === null) return; // 店铺仓库已处理
          const originalWh = originalWarehouses.find(w => w.warehouseId === ws.warehouseId);
          const beforeQty = originalWh?.stockQuantity || 0;
          const afterQty = ws.stockQuantity || 0;
          if (beforeQty !== afterQty) {
            records.push({
              productId: Number(this.productId),
              wholesalerId: Number(this.wholesalerId),
              warehouseId: ws.warehouseId,
              warehouseName: ws.warehouseName,
              adjustType: adjustType,
              changeQuantity: afterQty - beforeQty,
              beforeQuantity: beforeQty,
              afterQuantity: afterQty,
              reason: reason
            });
          }
        });
      }
      
      console.log('📝 构造库存流水:', records.length, '条');
      console.log('📝 调整类型:', adjustType, '原因:', reason);
      return records;
    },
    
    // 暂时注释：InventoryQueryRequest 缺少 productIds 字段导致 500 错误
    // async loadInventories() {
    //   try {
    //     const response = await api.post('/inventory/query', {
    //       productId: this.productId,
    //       page: 1,
    //       pageSize: 100
    //     });
    //     
    //     if (response.data && response.data.data) {
    //       this.inventories = response.data.data;
    //     } else {
    //       this.inventories = [];
    //     }
    //   } catch (error) {
    //     console.error('❌ 加载库存失败:', error);
    //   }
    // },

    async createProduct() {
      console.log('📝 开始创建商品，formData:', this.formData);
      
      if (!this.formData.name?.trim()) {
        alert('❌ 请输入商品名称');
        return;
      }
      
      if (!this.formData.marketPrice || this.formData.marketPrice <= 0) {
        alert('❌ 请输入有效的市场价，当前值：' + this.formData.marketPrice);
        return;
      }
      
      if (this.formData.costPrice === null || this.formData.costPrice === undefined || this.formData.costPrice === '') {
        alert('❌ 请输入成本价');
        return;
      }
      if (this.formData.costPrice < 0) {
        alert('❌ 成本价不能为负数');
        return;
      }
      
      console.log('🔍 验证前分类值:', this.formData.categoryName, '长度:', this.formData.categoryName?.length);
      
      if (!this.formData.categoryName?.trim()) {
        console.log('❌ 分类为空，阻止提交');
        alert('❌ 请选择商品分类（输入商品名称后会有 AI 推荐）');
        return;
      }
      
      console.log('✅ 分类验证通过:', this.formData.categoryName);
      
      try {
        // ⭐ 准备提交数据（添加 wholesalerId、price 和图片）
        const wholesalerId = this.wholesalerId;
        
        // ⭐ 自动生成 SKU（如果为空）
        let code = this.formData.code?.trim();
        if (!code) {
          // 使用时间戳 + 随机数生成唯一 SKU
          code = 'SKU_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
          console.log('🔑 自动生成 SKU:', code);
        }
        
        const submitData = {
          ...this.formData,
          code: code,  // 使用生成的 SKU
          wholesalerId: wholesalerId ? parseInt(wholesalerId) : null,
          // ⭐ price 字段必填（数据库 NOT NULL），使用 marketPrice 作为默认值
          price: this.formData.marketPrice || this.formData.price || 0,
          // ⭐ 图片处理：数组转 mainImage + images
          mainImage: this.formData.mainImages.length > 0 ? this.formData.mainImages[0] : null,
          images: this.formData.mainImages.length > 0 ? this.formData.mainImages : [],
          // ⭐ 税率信息（现场计算，不信 formData 缓存）
          ...this.getTaxInfo()
        };
        
        // 删除 formData 中的 mainImages 数组，避免混淆
        delete submitData.mainImages;
        
        // ⭐⭐⭐ 关键修复：写回 totalStock（创建前强制同步总库存）
        submitData.totalStock = this.totalWarehouseStock;
        
        console.log('📤 完整提交数据:', JSON.stringify(submitData, null, 2));
        console.log('📊 计算的总库存:', this.totalWarehouseStock);
        
        const result = await api.post('/product', submitData);
        console.log('✅ 后端返回:', result);
        
        // ✅ 获取新商品 ID
        const newProductId = result.id || result.data?.id;
        console.log('🆕 新商品 ID:', newProductId);
        
        // ✅ 同步 productId（防止后续用 create）
        this.productId = newProductId;
        
        // ✅ 创建商品后生成初始库存流水
        if (newProductId) {
          await this.generateInitialStockRecords(newProductId);
        }
        
        alert('✅ 创建成功！');
        
        setTimeout(() => {
          this.$router.push('/wholesaler/product');
        }, 1500);
      } catch (error) {
        console.error('❌ 创建失败:', error);
        console.error('❌ error.response:', error.response);
        console.error('❌ error.response.data:', error.response?.data);
        
        let errorMsg = error.response?.data?.message || error.response?.data || error.message || '未知错误';
        
        // 提取更详细的错误信息
        if (error.response?.data?.errors) {
          errorMsg = JSON.stringify(error.response.data.errors);
        }
        
        alert('❌ 创建失败：' + errorMsg + '\n\n状态码：' + (error.response?.status || '无'));
      }
    },

    async saveProduct() {
      if (this.saving) {
        console.log('⏳ 正在保存中，请稍候...');
        return;
      }
      
      if (!this.formData.name?.trim()) {
        this.showToastMsg('请输入商品名称');
        return;
      }
      
      if (!this.formData.marketPrice || this.formData.marketPrice <= 0) {
        this.showToastMsg('请输入有效的市场价');
        return;
      }
      
      if (this.formData.costPrice === null || this.formData.costPrice === undefined || this.formData.costPrice === '') {
        this.showToastMsg('请输入成本价');
        return;
      }
      if (this.formData.costPrice < 0) {
        this.showToastMsg('成本价不能为负数');
        return;
      }
      
      // ⭐ 检查库存调整是否填写完整（单仓库模式）
      if (this.showStockAdjustDialog && !this.formData.hasMultiWarehouse) {
        if (!this.stockAdjustment.adjustType) {
          this.showToastMsg('⚠️ 请选择库存调整类型');
          return;
        }
        if (!this.stockAdjustment.reason?.trim()) {
          this.showToastMsg('⚠️ 请填写库存调整原因');
          return;
        }
      }
      
      this.saving = true;
      this.showToastMsg('⏳ 正在保存...');
      
      try {
        // ⭐ 计算每个仓库的库存变化，生成流水记录
        let stockFlowRecords = [];
        
        // ✅ 1️⃣ 店铺仓库变化
        if (this.originalTotalStock !== null && this.originalWarehouses) {
          // 计算原始店铺库存 = originalTotalStock - 所有实际仓库的原始库存
          const originalWarehousesTotal = this.originalWarehouses.reduce(
            (sum, w) => sum + (Number(w.stockQuantity) || 0), 0
          );
          const originalShop = this.originalTotalStock - originalWarehousesTotal;
          
          const beforeShopQty = Number(originalShop) || 0;
          const afterShopQty = Number(this.formData.shopStockQuantity) || 0;
          const shopChange = Number((afterShopQty - beforeShopQty).toFixed(1));
          
          if (shopChange !== 0) {
            stockFlowRecords.push({
              productId: this.productId,
              wholesalerId: this.wholesalerId,
              warehouseId: null,
              warehouseName: '店铺仓库',
              adjustType: shopChange > 0 ? 'IN' : 'OUT',
              changeQuantity: Math.abs(shopChange),
              beforeQuantity: beforeShopQty,
              afterQuantity: afterShopQty,
              adjustTypeLabel: this.stockAdjustment.adjustType || (shopChange > 0 ? '进货' : '出库'),
              reason: this.stockAdjustment.reason?.trim() || '库存调整'
            });
            console.log(`🏪 店铺仓库变化：${shopChange > 0 ? '+' : ''}${shopChange}`);
          }
        }
        
        // ✅ 2️⃣ 实际仓库逐个判断
        if (this.formData.warehouses && this.formData.warehouses.length > 0 && this.originalWarehouses) {
          this.formData.warehouses.forEach((warehouse) => {
            // ⭐ 更安全写法：用 warehouseId 查找，不依赖数组顺序
            const originalWarehouse = this.originalWarehouses.find(
              w => w.warehouseId === warehouse.warehouseId
            ) || {};
            
            const beforeQty = originalWarehouse.stockQuantity || 0;
            const afterQty = warehouse.stockQuantity || 0;
            const change = Number((afterQty - beforeQty).toFixed(1));
            
            // 只有库存有变化才生成流水
            if (change !== 0) {
              stockFlowRecords.push({
                productId: this.productId,
                wholesalerId: this.wholesalerId,
                warehouseId: warehouse.warehouseId || null,
                warehouseName: warehouse.warehouseName,
                adjustType: change > 0 ? 'IN' : 'OUT',
                changeQuantity: Math.abs(change),
                beforeQuantity: beforeQty,
                afterQuantity: afterQty,
                adjustTypeLabel: this.stockAdjustment.adjustType || (change > 0 ? '进货' : '出库'),
                reason: this.stockAdjustment.reason?.trim() || '库存调整'
              });
              console.log(`📦 ${warehouse.warehouseName} 变化：${change > 0 ? '+' : ''}${change}`);
            }
          });
        }
        
        console.log('📊 库存流水记录:', stockFlowRecords.length, '条');
        stockFlowRecords.forEach(r => {
          console.log(`   - ${r.warehouseName}: ${r.adjustTypeLabel} ${r.changeQuantity}`);
        });
        
        // ⭐ 根据多仓库模式调用不同的保存方法
          await this.saveMultiWarehouse(stockFlowRecords);
        // } else {
        //   await this.saveSingleWarehouse();
        // }
        
        console.log('✅ 保存成功');
        this.showToastMsg('✅ 保存成功！');
        
        setTimeout(() => {
          this.$router.push('/wholesaler/product');
        }, 1500);
      } catch (error) {
        console.error('❌ 保存失败:', error);
        this.showToastMsg('❌ ' + (error.response?.data?.message || '保存失败'));
      } finally {
        this.saving = false;
      }
    },
    
    // 🏭 多仓库模式保存
    async saveMultiWarehouse() {
      
      // ⭐ 计算总库存 = 店铺仓库 + 所有实际仓库
      const totalStock = Number((
        (this.formData.shopStockQuantity || 0) + 
        (this.formData.warehouses || []).reduce((sum, w) => sum + (Number(w.stockQuantity) || 0), 0)
      ).toFixed(1));
      
      // ⭐ 构造 warehouseStocks（包含店铺仓库和所有实际仓库）
      const warehouseStocks = [
        {
          warehouseId: null,
          warehouseName: '店铺仓库',
          stockQuantity: Number(this.formData.shopStockQuantity) || 0  // ⭐ 店铺仓库库存
        },
        ...(this.formData.warehouses || []).map((w, idx) => {
          const stockQty = Number(w.stockQuantity) || 0;
          console.log(`   [${idx}] ${w.warehouseName}: stockQuantity=${stockQty} (原始值：${w.stockQuantity})`);
          return {
            id: w.id,
            warehouseId: w.warehouseId || null,
            warehouseName: w.warehouseName,
            stockQuantity: stockQty
          };
        })
      ];
      
      console.log('📦 仓库库存数据:', JSON.stringify(warehouseStocks, null, 2));
      console.log('📊 计算总库存:', totalStock);
      console.log('🔍 保存前检查：');
      console.log('   formData.shopStockQuantity:', this.formData.shopStockQuantity, '(类型:', typeof this.formData.shopStockQuantity, ')');
      console.log('   formData.totalStock:', this.formData.totalStock);
      console.log('   warehouses 数量:', this.formData.warehouses ? this.formData.warehouses.length : 0);
      
      // ⭐ 提交数据：基本信息 + 仓库库存 + 价格信息
      const submitData = {
        id: this.formData.id,
        name: this.formData.name,
        description: this.formData.description || '',
        categoryName: this.formData.categoryName || '',
        enabled: this.formData.enabled !== undefined ? this.formData.enabled : true,
        isHot: this.formData.isHot || false,
        isNew: this.formData.isNew || false,
        pinnedHome: this.formData.pinnedHome || false,
        pinnedSearch: this.formData.pinnedSearch || false,
        wholesalePriceVisible: this.formData.wholesalePriceVisible !== false,
        totalStock: totalStock,
        mainImage: this.formData.mainImages.length > 0 ? this.formData.mainImages[0] : null,
        images: this.formData.mainImages.length > 0 ? this.formData.mainImages : [], // ⭐ 多图列表
        
        // ⭐ 价格信息
        marketPrice: this.formData.marketPrice || 0,
        costPrice: this.formData.costPrice || 0,
        price: this.formData.marketPrice || this.formData.price || 0,
        
        // ⭐ 税率信息（现场计算，不信 formData 缓存）
        ...this.getTaxInfo(),
        
        // ⭐ 计量单位（中英双语）
        unit: this.formData.unit || '件 (Piece)',
        
        // ⭐ 店铺库存（单独传，后端直接存到 product.shop_stock_quantity）
        shopStockQuantity: Number(this.formData.shopStockQuantity) || 0,
        
        // ⭐ 二级分类、标签、别名
        subCategoryName: this.formData.subCategoryName || '',
        tags: this.formData.tags || '',
        aliases: this.formData.aliases || '',
        
        // ⭐ 仓库库存明细 + 调整原因 + 调整类型（后端自动生成流水）
        warehouseStocks: warehouseStocks,
        adjustReason: this.stockAdjustment.reason || '库存调整',
        adjustTypeLabel: this.stockAdjustment.adjustType || '盘点调整'
      };
      
      console.log('📤 提交数据:', JSON.stringify(submitData, null, 2));
      
      if (this.pageMode === 'create') {
        await api.post('/product', submitData);
      } else {
        await api.put(`/product/${this.productId}`, submitData);
      }
      
      console.log('✅ 多仓库模式保存成功');
    },
    
    // ✅ 生成初始库存流水（商品创建时）
    async generateInitialStockRecords(productId) {
      console.log('📝 生成初始库存流水，productId:', productId);
      
      let records = [];
      
      // 🏪 店铺仓库
      const shopQty = Number(this.formData.shopStockQuantity) || 0;
      if (shopQty > 0) {
        records.push({
          productId: Number(productId),
          wholesalerId: Number(this.wholesalerId),
          warehouseId: null,
          warehouseName: '店铺仓库',
          adjustType: 'IN',
          changeQuantity: shopQty,
          beforeQuantity: 0,
          afterQuantity: shopQty,
          adjustTypeLabel: '进货',
          reason: '商品创建初始入库'
        });
        console.log(`🏪 店铺仓库初始入库：${shopQty}`);
      }
      
      // 📦 实际仓库
      (this.formData.warehouses || []).forEach(w => {
        const qty = Number(w.stockQuantity) || 0;
        if (qty > 0) {
          records.push({
            productId: Number(productId),
            wholesalerId: Number(this.wholesalerId),
            warehouseId: w.warehouseId,
            warehouseName: w.warehouseName,
            adjustType: 'IN',
            changeQuantity: qty,
            beforeQuantity: 0,
            afterQuantity: qty,
            adjustTypeLabel: '进货',
            reason: '商品创建初始入库'
          });
          console.log(`📦 ${w.warehouseName} 初始入库：${qty}`);
        }
      });
      
      // ✅ 写入库存流水
      if (records.length > 0) {
        await this.saveStockRecord(records);
        console.log('✅ 初始库存流水已保存，共', records.length, '条');
      }
    },
    
    // 📦 单仓库模式保存
    async saveSingleWarehouse() {
      console.log('📦 单仓库模式，走原有保存流程');
      
      const warehouseStocks = [{
        warehouseId: null,
        warehouseName: '店铺仓库',
        stockQuantity: Number(this.formData.shopStockQuantity) || 0
      }];
      
      const submitData = {
        id: this.formData.id,
        name: this.formData.name,
        description: this.formData.description || '',
        categoryName: this.formData.categoryName || '',
        enabled: this.formData.enabled !== undefined ? this.formData.enabled : true,
        isHot: this.formData.isHot || false,
        isNew: this.formData.isNew || false,
        pinnedHome: this.formData.pinnedHome || false,
        pinnedSearch: this.formData.pinnedSearch || false,
        totalStock: Number(this.formData.shopStockQuantity) || 0,
        mainImage: this.formData.mainImages.length > 0 ? this.formData.mainImages[0] : null,
        images: this.formData.mainImages.length > 0 ? this.formData.mainImages : [], // ⭐ 多图列表
        
        // ⭐ 价格信息
        marketPrice: this.formData.marketPrice || 0,
        costPrice: this.formData.costPrice || 0,
        price: this.formData.marketPrice || this.formData.price || 0,
        
        // ⭐ 税率信息（现场计算，不信 formData 缓存）
        ...this.getTaxInfo(),
        
        shopStockQuantity: Number(this.formData.shopStockQuantity) || 0,
        
        warehouseStocks: warehouseStocks,
        adjustReason: this.stockAdjustment.reason || '库存调整'
      };
      
      console.log('📤 提交数据:', JSON.stringify(submitData, null, 2));
      
      if (this.pageMode === 'create') {
        await api.post('/product', submitData);
      } else {
        await api.put(`/product/${this.productId}`, submitData);
      }
      
      console.log('✅ 单仓库模式保存成功');
    },
    
    // ⭐ 重新计算总库存
    recalculateTotalStock() {
      // 总库存 = 店铺仓库库存 + 所有实际仓库库存
      const shopStock = w.shopStockQuantity || 0;
      const allStock = (this.formData.warehouses || []).reduce(
        (sum, w) => sum + (w.shopStockQuantity || 0), 
        0
      );
      this.formData.totalStock = Number((shopStock + allStock).toFixed(1));
    },
    
    // ⭐ 添加仓库
    addWarehouse() {
      this.formData.warehouses.push({
        warehouseName: '',
        stockQuantity: 0
      });
    },
    
    // ⭐ 删除仓库
    removeWarehouse(index) {
      if (index === 0) {
        this.showToastMsg('⚠️ 默认仓库不能删除');
        return;
      }
      if (confirm('确定要删除这个仓库吗？')) {
        this.formData.warehouses.splice(index, 1);
        this.recalculateTotalStock();
      }
    },
    
    // ⭐ 更新店铺仓库库存
    updateShopStock(val) {
      if (this.formData.warehouses && this.formData.warehouses.length > 0) {
        this.formData.warehouses[0].shopStockQuantity = Number(val);
      }
    },
    
    // ⭐ 记录原始库存（聚焦时）
    recordOriginalStock() {
      this.originalTotalStock = this.totalWarehouseStock;
      console.log('📝 记录原始库存:', this.originalTotalStock);
    },
    
    // ⭐ 仓库库存变化 - 弹出调整面板（失焦时）
    onWarehouseStockBlur() {
      if (this.originalTotalStock === null) {
        console.log('⚠️ originalTotalStock 未初始化');
        return;
      }
      
      const newTotal = this.totalWarehouseStock;
      const oldTotal = this.originalTotalStock;
      const change = Number((newTotal - oldTotal).toFixed(1));
      
      console.log('📦 库存变化检测（失焦）:', oldTotal, '->', newTotal, '变化:', change);
      
      // 库存有变化时弹出调整面板
      if (change !== 0) {
        this.stockAdjustment.changeQuantity = change;
        this.stockAdjustment.adjustType = '';
        this.stockAdjustment.reason = '';
        this.showStockAdjustDialog = true;
        console.log('✅ 弹出调整面板，变化量:', change);
      }
    },
    
    // ⭐ 保存库存流水记录（支持单条或多条）
    async saveStockRecord(records) {
      try {
        // 兼容两种调用方式：
        // 1. 单条记录：不传 records，使用 this.stockAdjustment
        // 2. 多条记录：传入 records 数组
        
        let recordList = [];
        
        if (records && Array.isArray(records)) {
          // 多条记录模式
          recordList = records;
          console.log('📝 批量保存库存流水:', recordList.length, '条');
        } else {
          // 单条记录模式（原有逻辑）
          const changeQuantity = this.stockAdjustment.changeQuantity;
          const afterQuantity = this.formData.totalStock;
          const adjustType = changeQuantity >= 0 ? 'IN' : 'OUT'; // 后端枚举：IN/OUT
          
          // ⭐ 使用统一方法获取 wholesalerId
          const wholesalerId = getWholesalerId() ?? 8;
          
          const recordData = {
            productId: this.productId,
            wholesalerId: wholesalerId, // ⭐ 使用 localStorage 的值
            warehouseId: null, // 单仓库模式为 null
            adjustType: adjustType, // ⭐ 必填：IN 或 OUT
            changeQuantity: Math.abs(changeQuantity), // ⭐ 必填：正数
            afterQuantity: afterQuantity, // ⭐ 必填：调整后库存
            adjustTypeLabel: this.stockAdjustment.adjustType || (changeQuantity > 0 ? '进货' : '出库'), // ⭐ 必填：中文类型
            reason: this.stockAdjustment.reason || '库存调整'
          };
          
          recordList = [recordData];
          console.log('📝 保存单条库存流水:', recordData);
        }
        
        // 批量保存所有流水记录
        const results = [];
        for (const record of recordList) {
          try {
            const result = await api.post('/inventory/record', record);
            results.push({ success: true, record, result });
          } catch (error) {
            results.push({ success: false, record, error });
            console.error('❌ 单条流水保存失败:', record, error);
          }
        }
        
        // 统计结果
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        
        console.log(`✅ 库存流水保存完成：成功 ${successCount} 条，失败 ${failCount} 条`);
        
        // 重置状态（单条模式）
        if (!records) {
          this.showStockAdjustDialog = false;
          this.originalStock = null;
        }
        
        // 重新加载库存流水
        this.loadStockHistory();
        
        return { successCount, failCount, results };
      } catch (error) {
        console.error('❌ 保存库存流水失败:', error);
        // 不阻止保存，只是记录失败
        this.showToastMsg('⚠️ 库存流水记录失败，但商品已保存');
        return { successCount: 0, failCount: 0, results: [], error };
      }
    },

    async handleMainImageUpload() {
      // 判断是否在 Capacitor 原生环境
      const isNative = window.Capacitor?.isNativePlatform?.() === true;
      const hasCamera = window.Capacitor?.Plugins?.Camera;
      
      // 如果在原生环境且有 Camera 插件，尝试使用原生相机
      if (isNative && hasCamera) {
        try {
          const { Camera, CameraResultType, CameraSource } = window.Capacitor.Plugins.Camera;
          
          const result = await Camera.getPhoto({
            quality: 80,
            allowEditing: false,
            resultType: CameraResultType.Uri,
            source: CameraSource.Prompt,
            correctOrientation: true,
          });
          
          if (result && result.webPath) {
            const response = await fetch(result.webPath);
            const blob = await response.blob();
            const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
            
            const uploadResult = await import('@/services/api').then(m => m.uploadFile('/files/upload-image', file));
            if (uploadResult && uploadResult.url) {
              const apiBaseUrl = process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL.replace('/api', '') : '/api';
              const imageUrl = uploadResult.url.startsWith('http') ? uploadResult.url : apiBaseUrl + uploadResult.url;
              this.formData.mainImages.push(imageUrl);
              console.log('🖼️ 图片已添加到临时列表，等待用户保存');
              this.showToastMsg('✅ 图片上传成功');
              return;
            }
          }
        } catch (error) {
          console.warn('⚠️ 原生相机失败，降级到文件选择器:', error);
          // 继续执行下面的降级逻辑
        }
      }
      
      // 降级方案：使用文件选择器（网页版或模拟器）
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        try {
          for (const file of files) {
            const result = await import('@/services/api').then(m => m.uploadFile('/files/upload-image', file));
            if (result && result.url) {
              const apiBaseUrl = process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL.replace('/api', '') : '/api';
              const imageUrl = result.url.startsWith('http') ? result.url : apiBaseUrl + result.url;
              this.formData.mainImages.push(imageUrl);
              console.log('🖼️ 图片已添加到临时列表，等待用户保存');
            }
          }
          this.showToastMsg(`✅ 上传了 ${files.length} 张图片`);
        } catch (error) {
          console.error('❌ 图片上传失败:', error);
          this.showToastMsg('图片上传失败，请重试');
        }
      };
      input.click();
    },

    // 立即保存商品（用于图片上传后）
    async saveProductImmediately() {
      try {
        const submitData = {
          ...this.formData,
          mainImage: this.formData.mainImages.length > 0 ? this.formData.mainImages[0] : null,
          images: this.formData.mainImages.length > 0 ? this.formData.mainImages : [], // ⭐ 多图列表
          ...this.getTaxInfo()
        };
        
        // 删除 formData 中的 mainImages 数组，避免混淆
        delete submitData.mainImages;
        
        console.log('📤 提交保存图片:', submitData);
        
        // 新建模式用 POST，编辑模式用 PUT
        if (this.pageMode === 'create') {
          await api.post('/product', submitData);
        } else {
          await api.put(`/product/${this.productId}`, submitData);
        }
        console.log('✅ 图片已保存到数据库');
      } catch (error) {
        console.error('❌ 保存图片失败:', error);
      }
    },

    openImageViewer(index) {
      this.currentViewImage = this.mainImages[index];
      this.showImageViewer = true;
    },

    closeImageViewer() {
      this.showImageViewer = false;
    },

    deleteImage(index) {
      if (confirm('确定要删除这张图片吗？')) {
        this.formData.mainImages.splice(index, 1);
        console.log('🗑️ 图片已删除，剩余:', this.formData.mainImages.length);
      }
    },

    showToastMsg(message) {
      this.toastMessage = message;
      this.showToast = true;
      
      setTimeout(() => {
        this.showToast = false;
      }, 2500);
    },

    goBack() {
      this.$router.go(-1);
    },

    translateFlowType(type) {
      const map = {
        '入库': this.$t('wholesaler.product.ProductDetail.flow_type.入库'),
        '出库': this.$t('wholesaler.product.ProductDetail.flow_type.出库'),
        '进货': this.$t('wholesaler.product.ProductDetail.flow_type.进货'),
        '损耗': this.$t('wholesaler.product.ProductDetail.flow_type.损耗'),
        '盘点': this.$t('wholesaler.product.ProductDetail.flow_type.盘点'),
        '其他': this.$t('wholesaler.product.ProductDetail.flow_type.其他'),
        '库存调整': this.$t('wholesaler.product.ProductDetail.flow_type.库存调整')
      };
      return map[type] || type;
    },

    getInventoryStatus(status) {
      if (status === 0) return 'normal';
      if (status === 1) return 'warning';
      if (status === 2) return 'out';
      return 'normal';
    },

    getInventoryStatusLabel(status) {
      const labels = {
        0: '正常',
        1: '预警',
        2: '缺货'
      };
      return labels[status] || '正常';
    },

    formatDate(dateString) {
      return formatDate(dateString);
    },
    
    // 格式化日期时间（置顶时间）
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    
    // 置顶到首页变化
    async onPinHomeChange() {
      // 创建模式下不调接口，随创建请求一起提交
      if (this.pageMode === 'create') return;
      // 检查 productId 是否是有效的数字 ID
      const productIdNum = Number(this.productId);
      if (!this.productId || isNaN(productIdNum) || productIdNum <= 0) {
        this.formData.pinnedHome = false;
        this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinInvalidId'));
        return;
      }
      try {
        const endpoint = this.formData.pinnedHome ? `/product/${this.productId}/pin-home` : `/product/${this.productId}/unpin-home`;
        await api.put(endpoint);
        if (this.formData.pinnedHome) {
          this.formData.pinnedHomeAt = new Date().toISOString();
          this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinHomeSuccess'));
        } else {
          this.formData.pinnedHomeAt = null;
          this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinHomeCancel'));
        }
      } catch (e) {
        this.formData.pinnedHome = !this.formData.pinnedHome;
        this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinOperationFailed'));
      }
    },
    
    // 置顶到搜索变化
    async onPinSearchChange() {
      // 创建模式下不调接口，随创建请求一起提交
      if (this.pageMode === 'create') return;
      // 检查 productId 是否是有效的数字 ID
      const productIdNum = Number(this.productId);
      if (!this.productId || isNaN(productIdNum) || productIdNum <= 0) {
        this.formData.pinnedSearch = false;
        this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinInvalidId'));
        return;
      }
      try {
        const endpoint = this.formData.pinnedSearch ? `/product/${this.productId}/pin-search` : `/product/${this.productId}/unpin-search`;
        await api.put(endpoint);
        if (this.formData.pinnedSearch) {
          this.formData.pinnedSearchAt = new Date().toISOString();
          this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinSearchSuccess'));
        } else {
          this.formData.pinnedSearchAt = null;
          this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinSearchCancel'));
        }
      } catch (e) {
        this.formData.pinnedSearch = !this.formData.pinnedSearch;
        this.showToastMsg(this.$t('wholesaler.product.ProductDetail.pinOperationFailed'));
      }
    },
    
    // 跳转到库存流水查询页
    goToStockFlowQuery() {
      this.$router.push({
        path: '/wholesaler/stock-flow',
        query: { productId: this.productId }
      });
    },
    
    // 加载商品列表（用于流水查询页默认选中）
    async loadProductList() {
      try {
        const res = await api.get('/product', {
          params: { page: 1, pageSize: 200 }
        });
        if (Array.isArray(res)) {
          this.productList = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          this.productList = res.data;
        } else if (res && res.content && Array.isArray(res.content)) {
          this.productList = res.content;
        } else {
          this.productList = [];
        }
      } catch (error) {
        console.error('加载商品列表失败:', error);
        this.productList = [];
      }
    }
  }
};
</script>

<style scoped>
.product-detail-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.back-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
}

.btn-save {
  padding: 8px 16px;
  border: none;
  background: var(--primary, #3498db);
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px;
}

.info-section {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.info-section h3 {
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.form-item .category-select,
.form-item .stock-input-wrapper {
  width: 100%;
}

.form-item input,
.form-item textarea,
.form-item select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-item input:focus,
.form-item textarea:focus,
.form-item select:focus {
  outline: none;
  border-color: var(--primary, #3498db);
}

.form-item input:disabled,
.form-item textarea:disabled,
.form-item select:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.description-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.description-textarea:focus {
  outline: none;
  border-color: var(--primary, #3498db);
}

.description-textarea:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

/* 模板搜索下拉样式 */
.template-search-wrapper {
  position: relative;
}

.template-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 4px;
}

.template-dropdown-header {
  padding: 8px 12px;
  background: #f0f5ff;
  border-bottom: 1px solid #e0e7ff;
  border-radius: 8px 8px 0 0;
}

.template-dropdown-title {
  font-size: 12px;
  font-weight: 600;
  color: #1890ff;
}

.template-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.template-item:last-child {
  border-bottom: none;
}

.template-item:hover {
  background-color: #f5f9ff;
}

.template-image {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  margin-right: 10px;
  flex-shrink: 0;
}

.template-image-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-right: 10px;
  flex-shrink: 0;
}

.template-info {
  flex: 1;
  min-width: 0;
}

.template-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.template-badge {
  display: inline-block;
  padding: 2px 6px;
  background-color: #e6f7ff;
  color: #1890ff;
  font-size: 11px;
  border-radius: 4px;
  font-weight: 600;
  flex-shrink: 0;
}

.template-meta {
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 8px;
}

.template-meta span {
  display: inline-block;
}

.image-gallery {
  margin-bottom: 12px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
}

.gallery-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
}

.btn-delete-image {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff4d4f;
  color: #fff;
  border: 2px solid #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-delete-image:hover {
  background: #ff7875;
  transform: scale(1.1);
}

.image-actions {
  margin-top: 12px;
}

.no-image {
  color: #999;
  font-size: 13px;
  padding: 20px;
  text-align: center;
  background: #f9f9f9;
  border-radius: 8px;
}

.btn-upload {
  padding: 8px 16px;
  border: 1px dashed #ddd;
  background: #fff;
  color: #666;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
}

.btn-upload:hover {
  border-color: var(--primary, #3498db);
  color: var(--primary, #3498db);
}

.stock-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.inventory-table-wrapper {
  margin: 16px 0;
  overflow-x: auto;
}

.inventory-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.inventory-table thead {
  background: #f5f7fa;
  border-bottom: 2px solid #ddd;
}

.inventory-table th {
  padding: 10px;
  text-align: left;
  font-weight: 600;
  color: #666;
}

.inventory-table td {
  padding: 10px;
  border-bottom: 1px solid #eee;
  color: #666;
}

.inventory-table .warehouse-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.inventory-table .warehouse-id {
  font-size: 11px;
  color: #999;
}

.inventory-table .stock-value {
  font-weight: 600;
  color: #fa8c16;
}

.inventory-table .available-value {
  font-weight: 600;
  color: #52c41a;
}

.inventory-table .status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
}

.inventory-table .status-normal {
  background-color: #d4edda;
  color: #155724;
}

.inventory-table .status-warning {
  background-color: #fff3cd;
  color: #856404;
}

.inventory-table .status-out {
  background-color: #f8d7da;
  color: #721c24;
}

.empty-hint {
  padding: 20px;
  text-align: center;
  color: #999;
}

.empty-hint p {
  margin-bottom: 16px;
}

.btn-manage-inventory {
  display: inline-block;
  padding: 10px 20px;
  background: #E3F2FD;
  color: #1976D2;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-size: 13px;
}

.btn-manage-inventory:hover {
  background: #BBDEFB;
}

.inventory-list {
  display: grid;
  gap: 12px;
}

.inventory-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
}

.warehouse-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.stock-quantity {
  font-size: 16px;
  font-weight: 700;
}

.stock-quantity .normal {
  color: #52c41a;
}

.stock-quantity .warning {
  color: #faad14;
}

.stock-quantity .out {
  color: #ff4d4f;
}

.no-inventory {
  color: #999;
  font-size: 13px;
  padding: 20px;
  text-align: center;
}

.loading-state {
  padding: 60px 20px;
  text-align: center;
  color: #999;
}

.loading-spinner {
  font-size: 48px;
  margin-bottom: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  background: rgba(0,0,0,0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  opacity: 0;
  transition: all 0.3s;
  z-index: 2000;
  pointer-events: none;
}

.toast.show {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.image-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.viewer-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.viewer-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
}

.viewer-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  padding: 8px;
}
/* Multi-Warehouse Styles */
.stock-input-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stock-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  height: 42px;
  box-sizing: border-box;
}

.stock-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.stock-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.btn-multi-warehouse {
  padding: 11px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  height: 42px;
  display: flex;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-multi-warehouse:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.multi-warehouse-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  background: #f0f2f5;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

/* 库存调整提示 */
.stock-adjust-hint {
  margin-top: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 8px;
  border: 2px solid #0ea5e9;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.adjust-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #bae6fd;
}

.adjust-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.adjust-value {
  font-size: 18px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 4px;
}

.adjust-value.positive {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.adjust-value.negative {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.adjust-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.adjust-type-select,
.adjust-reason-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  transition: border-color 0.2s;
}

.adjust-type-select:focus,
.adjust-reason-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.adjust-reason-input::placeholder {
  color: #94a3b8;
}

.adjust-reason-box {
  margin-top: 12px;
}

/* 仓库列表样式 */
.warehouse-section {
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.warehouse-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.warehouse-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.warehouse-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

/* 店铺仓库特殊样式 - 柔和区分 */
.warehouse-item.shop-warehouse {
  background: #fafafa;
  border-left: 3px solid #667eea;
}

.warehouse-item.shop-warehouse .warehouse-name {
  font-weight: 600;
  color: #666;
}

.warehouse-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.warehouse-icon {
  font-size: 18px;
}

.warehouse-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.warehouse-stock {
  display: flex;
  align-items: center;
  gap: 8px;
}

.warehouse-stock .stock-input {
  width: 100px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  text-align: right;
}

.warehouse-stock .unit {
  font-size: 14px;
  color: #6b7280;
}

.warehouse-stock .btn-remove {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fee2e2;
  color: #ef4444;
  border: none;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.warehouse-stock .btn-remove:hover {
  background: #fecaca;
  transform: scale(1.1);
}

.total-stock {
  margin-top: 16px;
  padding: 12px;
  background: #eff6ff;
  border-radius: 6px;
  text-align: right;
  font-size: 15px;
  color: #1e40af;
}

.btn-add-warehouse {
  margin-top: 12px;
  width: 100%;
  padding: 10px;
  background: #fff;
  border: 1px dashed #667eea;
  color: #667eea;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-warehouse:hover {
  background: #f5f3ff;
  border-color: #764ba2;
}

/* 库存流水 */
.stock-history-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.stock-history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stock-history-item {
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #0ea5e9;
}

.stock-history-item .history-type.in {
  color: #10b981;
  border-left-color: #10b981;
}

.stock-history-item .history-type.out {
  color: #ef4444;
  border-left-color: #ef4444;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.history-type {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 12px;
  background: rgba(14, 165, 233, 0.1);
  border-radius: 4px;
}

.history-date {
  font-size: 13px;
  color: #9ca3af;
}

.history-body {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}

.history-quantity {
  font-weight: 600;
  color: #374151;
}

.history-arrow {
  color: #9ca3af;
}

.history-after {
  color: #6b7280;
}

.history-remark {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
}

/* 库存流水区块 */
.stock-flow-section {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.btn-more {
  padding: 4px 12px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.btn-more:hover {
  border-color: var(--primary, #3498db);
  color: var(--primary, #3498db);
}

.stock-flow-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.stock-flow-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stock-flow-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #e2e8f0;
}

.stock-flow-item .flow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.stock-flow-item .flow-type {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.stock-flow-item .flow-type.in {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.stock-flow-item .flow-type.out {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.stock-flow-item .flow-date {
  font-size: 12px;
  color: #999;
}

.stock-flow-item .flow-body {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex-wrap: wrap;
}

.flow-warehouse {
  color: #666;
}

.flow-change {
  font-weight: 700;
}

.flow-change.positive {
  color: #10b981;
}

.flow-change.negative {
  color: #ef4444;
}

.flow-before-after {
  color: #999;
  font-size: 12px;
}

.flow-reason {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  padding-left: 8px;
  border-left: 2px solid #e5e7eb;
}

/* ⭐ 税率选择提示 */
.tax-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border-radius: 6px;
  font-size: 12px;
  color: #0369a1;
  border-left: 3px solid #38bdf8;
}

/* ⭐ 置顶设置 */
.pin-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.pin-section h4 {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.pin-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pin-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
}

.pin-option:hover {
  background: #f0f9ff;
  border-color: #38bdf8;
}

.pin-option input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #667eea;
}

.pin-option input[type='checkbox']:checked + .pin-label {
  color: #667eea;
  font-weight: 600;
}

.pin-label {
  color: #555;
  font-weight: 500;
  transition: color 0.2s;
}

.pin-time {
  font-size: 11px;
  color: #999;
}

/* ⭐ 标签/别名输入样式 */
.tag-input-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  min-height: 42px;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #e8f4fd;
  color: #1890ff;
  border-radius: 4px;
  font-size: 13px;
}

.tag-item.alias {
  background: #f0f5ff;
  color: #2f54eb;
}

.tag-remove {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  opacity: 0.6;
}

.tag-remove:hover {
  opacity: 1;
}

.tag-input {
  border: none;
  outline: none;
  padding: 4px 8px;
  font-size: 13px;
  min-width: 80px;
  flex: 1;
}

.btn-add-tag {
  padding: 4px 12px;
  background: #f5f5f5;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}

.btn-add-tag:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.hint-text {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
}

/* ⭐ 分享按钮 */
.btn-share {
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-share:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-share:active {
  transform: translateY(0);
}

</style>
